import { formatterValueType } from "@imminently/decisively-core";
import type { AttributeOption } from "@imminently/decisively-ui/AttributePicker";
import { useMemo } from "react";

const GLOBAL_GROUP = "Global";

/**
 * The attributes a message template can reference, from a parsed release graph.
 *
 * The key is the node description, because that's what the server's template
 * renderer looks up (`processVar` in decisively-core matches descriptions
 * case-insensitively), and what the old `TemplateField` suggested. Grouped by
 * entity, with foreign keys left out and duplicate descriptions collapsed.
 */
export const graphTemplateAttributes = (graph: any): { attributes: AttributeOption[]; groupOrder: string[] } => {
  const byDescription = new Map<string, AttributeOption>();

  for (const nodeId of graph?.nodes?.() ?? []) {
    const node = graph.node(nodeId);
    const description = typeof node?.description === "string" ? node.description.trim() : "";
    if (!description || node?.fk) continue;

    const lookup = description.toLowerCase();
    if (byDescription.has(lookup)) continue;

    const entity = typeof node.entity === "string" && node.entity !== "global" ? node.entity : GLOBAL_GROUP;
    byDescription.set(lookup, { key: description, label: description, group: entity, type: formatterValueType(node.type) });
  }

  const attributes = [...byDescription.values()];
  const groups = new Set(attributes.map((attribute) => attribute.group));
  const groupOrder = [
    ...(groups.has(GLOBAL_GROUP) ? [GLOBAL_GROUP] : []),
    ...[...groups].filter((group) => group !== GLOBAL_GROUP).sort((a, b) => a.localeCompare(b)),
  ];

  return { attributes, groupOrder };
};

export const useTemplateAttributes = (graph: any) => useMemo(() => graphTemplateAttributes(graph), [graph]);
